import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { cloneDeep, each, find, forEach, get, last } from 'lodash';

import { BaseWizardService } from '../base.wizard.service';
import { ActiveTestWizardStep } from './active.test.wizard.step';
import {
  ApiService,
  InputDatastoreService,
  CountryListService,
  DialogService,
  ValuesExchangeProvider,
  LabelsService,
  CommonRegEx
} from '@nrwl/common-lib';

const stepId = {
  active_test: 'wizardService.active_test',
  desc: 'wizardService.active_test.activetest_desc',
  originator: 'wizardService.active_test.activetest_originator',
  over_view: 'wizardService.active_test.activetest_overview'
};

const No_Proxy = 'No Proxy Selected';
@Injectable()
export class ActiveTestWizardService extends BaseWizardService implements OnDestroy {
  state: ActivatedRoute;

  subContentApplicationChangeEvent: Subject<void> = new Subject();
  subContentOriginatorChangeEvent: Subject<void> = new Subject();
  subContentDestinationChangeEvent: Subject<void> = new Subject();
  submitProxy: Subject<void> = new Subject();
  groupKey = 'codecs';
  noProxy = No_Proxy;
  existingVoipCodecs = [];
  activeTestTypes = [
    {
      id: 'service-continuity',
      description: 'Monitor the performance of a defined application',
      iconUrl: 'images/services.svg',
      name: 'Service'
    },
    {
      id: 'network-quality',
      description: 'Monitor network connections between two sites',
      iconUrl: 'images/network.svg',
      name: 'Network'
    },
    {
      id: 'voip',
      description: 'Monitor voice call quality between two sites',
      iconUrl: 'images/voip.svg',
      name: 'VoIP'
    }
  ];

  constructor(
    apiService: ApiService,
    inputDatastoreService: InputDatastoreService,
    state: ActivatedRoute,
    countryListService: CountryListService,
    dialogService: DialogService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly labelsService: LabelsService
  ) {
    super(apiService, inputDatastoreService, countryListService, dialogService);
    this.state = state;
    this.stepClass = ActiveTestWizardStep;
  }

  openWizard(
    wizardId: any, //eslint-disable-line
    wizardConfig: any, //eslint-disable-line
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
  ): void {
    super.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
    if (this.editMode) {
      this.currentStep = last(this.steps);
    } else if (this.defaultParamsById?.testTypes?.id) {
      this.output['testType'] = this.defaultParamsById.testTypes.id;
    }
  }

  ngOnDestroy() {
    //eslint-disable-line
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  setNextStep(): void {
    if (
      this.currentStep.id === stepId.desc &&
      (this.output.testType === 'network-quality' || this.output.testType === 'voip')
    ) {
      // skip agent description page for network-quality and voip
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this.currentStep = this.steps[this.currentStep.config.index + 2];
    } else if (
      this.currentStep.id === stepId.originator &&
      this.output.testType === 'service-continuity'
    ) {
      // skip 'destination' for service monitors
      this.currentStep = this.steps[this.currentStep.config.index + 2];
    } else {
      super.setNextStep();
    }
  }

  //eslint-disable-next-line
  goToStep(stepName: any): void {
    super.goToStep(stepName);
    // Disable the back btn to prevent active test type edit
    this.currentStep.disableBackBtn = false;
    if (this.currentStep.id === stepId.desc) {
      this.currentStep.disableBackBtn = true;
    }
  }
  nextStep(): void {
    //eslint-disable-line
    super.nextStep();
  }

  _setPreviousStep(): void {
    if (
      this.currentStep.id === stepId.originator &&
      (this.output.testType === 'network-quality' || this.output.testType === 'voip')
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 2];
    } else if (
      this.currentStep.id === stepId.over_view &&
      this.output.testType === 'service-continuity'
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 2];
    } else {
      super._setPreviousStep();
    }

    // Disable the back btn to prevent active test type edit
    this.currentStep.disableBackBtn = false;
    if (this.currentStep.id === stepId.desc) {
      this.currentStep.disableBackBtn = true;
    }
  }
  //eslint-disable-next-line
  renderValueFromMetaData(key: any): any {
    //eslint-disable-next-line
    const data: any = find(this.output.metaData, { key: key });
    if (data) {
      const readableLabel = this.labelsService.getLabel(key + '.' + data.value);
      if (readableLabel) {
        return readableLabel.label;
      }
    }
    return null;
  }

  //eslint-disable-next-line
  renderLabelFromMetaData(key: any): any {
    //eslint-disable-next-line
    const data: any = find(this.output.metaData, { key: key });
    if (data) {
      const readableLabel = this.labelsService.getLabel(key + '.' + data.value);
      return readableLabel;
    }
    return null;
  }

  getWebProxy(): string {
    let metaDataInfo = get(this.currentStep.output, 'metaData', []);
    //eslint-disable-next-line
    let webProxyInfo: any = find(metaDataInfo, { key: 'web-proxy' });
    if (webProxyInfo === undefined) {
      metaDataInfo = get(this.output, 'metaData', []);
      webProxyInfo = find(metaDataInfo, { key: 'web-proxy' });
    }
    if (webProxyInfo && webProxyInfo.value) {
      const proxy = JSON.parse(webProxyInfo.value);
      if (proxy.config) {
        if (proxy.config.useDefault) {
          return 'Default';
        }
        if (proxy.config.protocol && proxy.config.address && proxy.config.port) {
          return `${proxy.config.protocol}://${proxy.config.address}:${proxy.config.port}`;
        }
      }
    }
    return 'None';
  }

  //eslint-disable-next-line
  getSIPProxy(key: any): string {
    // Takes meta data from current step to show on originator/destination step
    let metaDataInfo = get(this.currentStep.output, 'metaData', []);
    //eslint-disable-next-line
    let sipProxyInfo: any = find(metaDataInfo, { key });
    if (sipProxyInfo === undefined) {
      metaDataInfo = get(this.output, 'metaData', []);
      sipProxyInfo = find(metaDataInfo, { key });
    }
    if (sipProxyInfo && sipProxyInfo.value) {
      const proxy = sipProxyInfo.value.includes('{')
        ? JSON.parse(sipProxyInfo.value)
        : sipProxyInfo.value;

      if (proxy['sip-proxy'] && proxy['sip-proxy'] !== No_Proxy) {
        const sipProxy = proxy['sip-proxy'];
        const proxyServerConfig = sipProxy['proxy-server-config'];
        return `SIP Proxy : ${sipProxy.alias} ${proxyServerConfig.transport}://${proxyServerConfig.address}:${proxyServerConfig.port}`;
      } else {
        return 'SIP Proxy : ' + No_Proxy;
      }
    } else {
      return 'SIP Proxy : ' + No_Proxy;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getTurnServerDetails(sipKey: string, metaData: any): string {
    let stunServerDetails: '';
    const SIPValues = metaData.find(key => key.key === sipKey);
    if (SIPValues && SIPValues.value && !SIPValues.value.includes(No_Proxy)) {
      const proxy = JSON.parse(SIPValues.value);
      stunServerDetails = proxy['sip-proxy']?.['nat-traversal']['stun-server'];
      return stunServerDetails;
    }
    return stunServerDetails;
  }
}
